/**
 * Theme management para el módulo Inventario.
 *
 * Estrategia de detección (en orden de prioridad):
 *   1. Override manual del user (toggle en avatar dropdown) — gana siempre,
 *      persiste en localStorage como 'user'-source.
 *   2. Hint del parent vía postMessage (si Whatsfy algún día decide
 *      cooperar — ver §"Detección desde el iframe" abajo).
 *   3. prefers-color-scheme del SO del usuario.
 *
 * Detección desde el iframe:
 *   Whatsfy NO expone una variable de sistema {{theme}} en su URL substitution
 *   (verificado contra docs.chatrace.com/kb/chatrace-system-variables/), y la
 *   same-origin policy impide leer .classList del parent. La única vía
 *   cooperativa sería postMessage. Este módulo deja la puerta abierta: si en
 *   el futuro Whatsfy hace `iframe.contentWindow.postMessage({type:'whatsfy:theme', value:'dark'}, '*')`
 *   al togglar, lo aplicamos automáticamente — siempre que el user no haya
 *   hecho override manual.
 *
 *   Lista de orígenes confiables del parent: *.chatrace.com, *.whatsfy.co.
 *   Cualquier otro origen es ignorado (defensa contra clickjacking de tema).
 */

export type Theme = 'light' | 'dark';
type Source = 'user' | 'auto';

const STORAGE_KEY = 'inv:theme';
const STORAGE_KEY_SOURCE = 'inv:theme:source';

/** Lee la decisión inicial del tema sin tocar el DOM. */
function resolveInitialTheme(): { theme: Theme; source: Source } {
  const saved = localStorage.getItem(STORAGE_KEY);
  const savedSource = localStorage.getItem(STORAGE_KEY_SOURCE);

  if (saved === 'dark' || saved === 'light') {
    return { theme: saved, source: savedSource === 'user' ? 'user' : 'auto' };
  }

  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return { theme: prefersDark ? 'dark' : 'light', source: 'auto' };
}

/**
 * Aplica un tema al <html>. Llamar antes del primer render para evitar
 * flash of wrong theme.
 */
export function initTheme(): Theme {
  const { theme } = resolveInitialTheme();
  document.documentElement.setAttribute('data-theme', theme);
  return theme;
}

/**
 * Setea un tema explícitamente (toggle manual del user).
 * Marca el origen como 'user' → no podrá ser sobrescrito por hint del parent.
 */
export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem(STORAGE_KEY, theme);
  localStorage.setItem(STORAGE_KEY_SOURCE, 'user');
}

/**
 * Aplica un tema sugerido por el parent (postMessage). Solo tiene efecto si
 * el user NO ha hecho override manual — respetar la voluntad explícita
 * del usuario es prioridad. Devuelve true si lo aplicó, false si fue saltado.
 */
function applyParentHint(theme: Theme): boolean {
  const savedSource = localStorage.getItem(STORAGE_KEY_SOURCE);
  if (savedSource === 'user') return false;
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem(STORAGE_KEY, theme);
  localStorage.setItem(STORAGE_KEY_SOURCE, 'auto');
  return true;
}

/**
 * Vuelve a seguir el tema del parent (limpia el flag 'user' del source).
 * Usado por el toggle "seguir tema del panel" en el avatar dropdown.
 */
export function followParentTheme(): void {
  localStorage.removeItem(STORAGE_KEY_SOURCE);
  localStorage.removeItem(STORAGE_KEY);
  // Re-evalúa: si no hay saved, vuelve a prefers-color-scheme. Cuando llegue
  // el próximo postMessage del panel, lo sigue.
  const { theme } = resolveInitialTheme();
  document.documentElement.setAttribute('data-theme', theme);
}

/** Indica si el user tiene override manual activo. */
export function hasManualOverride(): boolean {
  return localStorage.getItem(STORAGE_KEY_SOURCE) === 'user';
}

/**
 * Fuerza light mode en el documento. Usado por el storefront público
 * (`/tienda/*`) que NUNCA debe heredar el dark mode pensado para el panel
 * admin. No persiste el cambio (no toca localStorage) — al volver al admin,
 * el theme del user vuelve a aplicarse en el próximo `initTheme()`/refresh.
 *
 * No-op si `document` no existe (SSR safety, aunque hoy no aplica).
 */
export function forceLightMode(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', 'light');
  document.documentElement.classList.remove('dark');
  if (document.body) {
    document.body.classList.remove('dark');
  }
}

/**
 * True si el pathname actual corresponde a una ruta pública cara al cliente
 * final. Estas rutas se sirven light-only para que el branding del tenant
 * (acent color, logo) se vea consistente sin importar el `prefers-color-scheme`
 * del navegador del cliente.
 *
 * Rutas cubiertas:
 *   - `/tienda/*` — storefront browseable V3.1
 *   - `/realizar-pedido/*` — landing-pedido V3.0 (entry point del bot)
 */
export function isStorefrontPath(pathname: string): boolean {
  return (
    pathname.startsWith('/tienda/') ||
    pathname === '/tienda' ||
    pathname.startsWith('/realizar-pedido/') ||
    pathname === '/realizar-pedido'
  );
}

// Whitelist env-driven: VITE_TRUSTED_PARENT_ORIGINS comma-separated.
// Cada item se trata como sufijo de host (e.g. 'whatsfy.co' → '*.whatsfy.co').
// Default cubre el caso piloto Whatsfy; otros whitelabels deben
// extender vía env sin tocar código.
const DEFAULT_TRUSTED_HOST_SUFFIXES = ['chatrace.com', 'whatsfy.co', 'app.whatsfy.co'];

function parseTrustedHostSuffixes(): string[] {
  const raw = (import.meta.env as Record<string, string | undefined>)
    .VITE_TRUSTED_PARENT_ORIGINS;
  if (!raw) return DEFAULT_TRUSTED_HOST_SUFFIXES;
  const parsed = raw
    .split(',')
    .map((s) => s.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, ''))
    .filter(Boolean);
  return parsed.length > 0 ? parsed : DEFAULT_TRUSTED_HOST_SUFFIXES;
}

const TRUSTED_HOST_SUFFIXES = parseTrustedHostSuffixes();

// Cache de orígenes ya rechazados para no spamear consola con el mismo warn.
const warnedOrigins = new Set<string>();

function isTrustedOrigin(origin: string): boolean {
  let host: string;
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:') return false;
    host = url.host.toLowerCase();
  } catch {
    return false;
  }
  for (const suffix of TRUSTED_HOST_SUFFIXES) {
    const s = suffix.toLowerCase();
    if (host === s || host.endsWith('.' + s)) return true;
  }
  if (!warnedOrigins.has(origin)) {
    warnedOrigins.add(origin);
    console.warn(
      `[whatsfy-theme] origin not trusted, ignored: ${origin} (allowed suffixes: ${TRUSTED_HOST_SUFFIXES.join(', ')})`,
    );
  }
  return false;
}

interface ParentThemeMessage {
  type: 'whatsfy:theme';
  value: Theme;
}

function isParentThemeMessage(data: unknown): data is ParentThemeMessage {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return (
    obj.type === 'whatsfy:theme' && (obj.value === 'dark' || obj.value === 'light')
  );
}

/**
 * Suscribe el iframe a hints del parent. Idempotente — solo registra el
 * listener una vez. Solo activa cuando estamos efectivamente embebidos
 * (window !== window.top); en standalone no hay parent que escuchar.
 */
let subscribed = false;
export function subscribeToParentTheme(): void {
  if (subscribed) return;
  if (window === window.top) return;
  subscribed = true;

  window.addEventListener('message', (event: MessageEvent) => {
    // Pre-filtro: solo nos interesan messages tipados como whatsfy:theme.
    if (!isParentThemeMessage(event.data)) return;
    if (!isTrustedOrigin(event.origin)) return;
    applyParentHint(event.data.value);
  });

  // Race condition: el snippet del panel host puede haber emitido el theme
  // ANTES de que este listener estuviese registrado (orden de carga del
  // iframe vs. parent). Pedimos el tema actual de vuelta para que el host
  // reenvíe inmediatamente. targetOrigin '*' es seguro: el message no lleva
  // info sensible, solo el tipo. El host responderá únicamente al SPA_ORIGIN
  // configurado en su snippet, así que no hay leak.
  if (window.parent && window.parent !== window) {
    try {
      window.parent.postMessage({ type: 'whatsfy:theme:request' }, '*');
    } catch {
      /* no-op: si el parent no acepta postMessage (raro), seguimos con prefers-color-scheme */
    }
  }
}
